/* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/no-unnecessary-condition */
import { error } from 'common/errors';
import { markEnd, markStart } from 'common/instrumentation';
import { CommunicatorState, getState, saveApiResponseCache } from 'orch-communicator/store';
import { getSupportedCardNetworks } from 'orch-communicator/store/selectors';
import { getDcfCardBrand } from 'orch-communicator/utils/checkout';
import { dynamicDataTypeMapping } from 'orch-communicator/utils/dynamic-data-type';
import { getCheckoutActionCode } from 'orch-communicator/utils/get-checkout-actioncode';
import { addMissingProperties } from 'orch-communicator/utils/get-dpa-transaction-options';
import { validateInitResponses } from 'orch-communicator/utils/validate-init-responses';
import { BindingStatus, CheckoutData } from 'types/vsb-sdk/checkout';
import { generateCreateIntentRequestStr } from 'vsb-sdk/utils/checkout';
import { dpaId } from 'vsb-sdk/utils/flags';
import getBrowserName from '../../orch-communicator/utils/browser-name';
//START GENAI@CHATGPT4
//START GENAI@CHATGPT4
//START GENAI@GHCOPILOT
//START GENAI@GHCOPILOT

function checkReturningUser(): boolean {
  const state = getState().srcSdks.apiResponseCache;
  return state.idTokens.idTokens.length > 0 || state.completeIdentityValidation !== undefined;
}

function validateInput(rawInput: CheckoutData, isReturningUser: boolean): void {
  if (
    !isReturningUser &&
    (!rawInput.consumer?.mobileNumber?.phoneNumber || !rawInput.consumer?.fullName)
  ) {
    throw error(
      'INVALID PARAMETER',
      'One or more required parameters missing, please check missing fields',
      '' as any
    );
  }
}

function setIdToken(rawInput: CheckoutData & { idToken?: string }, isReturningUser: boolean): void {
  if (isReturningUser) {
    const state = getState().srcSdks.apiResponseCache;
    rawInput.idToken = state.idTokens.idTokens[0] || state.completeIdentityValidation;
  }
}

function setConsumerLocale(rawInput: CheckoutData): void {
  if (rawInput.consumer?.locale !== undefined) {
    rawInput.consumer.languageCode = rawInput.consumer.locale;
    delete rawInput.consumer.locale;
  }
}

function determineCardBrand(rawInput: CheckoutData): string | null {
  if (rawInput.encryptedCard && !rawInput.srcDigitalCardId) {
    return 'VISA';
  }

  const profiles = getState().srcSdks.apiResponseCache.getSrcProfile;
  return getDcfCardBrand(profiles, rawInput.srcDigitalCardId);
}

function validateCardBrand(cardBrand: string | null): void {
  const cardBrands = getSupportedCardNetworks();

  if (!cardBrand || (cardBrands && !cardBrands.includes(cardBrand))) {
    throw error('CARD_NOT_RECOGNIZED', `The provided srcDigitalCardId is not recognized.`);
  }
}

function decorateVisaCheckoutData(modifiedInput: CheckoutData): void {
  modifiedInput.vsbFlow = true;
  // === TLVS1-96 DEBUG: VSB flow flag set ===
  /*eslint-disable-next-line */
  console.log('[VSB] [TLVS1-96] vsbFlow flag set to true - using VSB authentication path');
  const authenticationContext: any = {};
  let flag = false;

  if (modifiedInput.authenticationMethod) {
    flag = true;

    if (modifiedInput.authenticationReasons) {
      authenticationContext.authenticationReasons = modifiedInput.authenticationReasons;
    }

    const { dpaTransactionOptions } = modifiedInput;

    if (dpaTransactionOptions) {
      if (dpaTransactionOptions.acquirerBIN) {
        authenticationContext.acquirerBIN = dpaTransactionOptions.acquirerBIN;
        delete dpaTransactionOptions.acquirerBIN;
      }

      if (dpaTransactionOptions.acquirerMerchantId) {
        authenticationContext.acquirerMerchantId = dpaTransactionOptions.acquirerMerchantId;
        delete dpaTransactionOptions.acquirerMerchantId;
      }

      if (dpaTransactionOptions.merchantName) {
        authenticationContext.merchantName = dpaTransactionOptions.merchantName;
        delete dpaTransactionOptions.merchantName;
      }

      authenticationContext.dpaTransactionOptions = dpaTransactionOptions;
    }
  }

  if (flag) {
    (modifiedInput as any).authenticationContext = authenticationContext;
  }
}

function getDynamicDataType(
  modifiedInput: CheckoutData,
  cardBrand: string | null
): string | undefined {
  if (cardBrand) {
    const paymentOptions: any = modifiedInput.dpaTransactionOptions?.paymentOptions;

    if (paymentOptions?.length > 0) {
      return dynamicDataTypeMapping[cardBrand][paymentOptions[0].dynamicDataType as string];
    }
  }

  return undefined;
}

function updatePaymentOptions(
  modifiedInput: CheckoutData,
  cardBrand: string | null,
  dynamicDataType: string | undefined
): void {
  let paymentOptions: any = modifiedInput.dpaTransactionOptions?.paymentOptions;

  if (dynamicDataType && paymentOptions) {
    if (cardBrand === 'VISA') {
      paymentOptions = paymentOptions.map((option: any) => ({
        ...option,
        dynamicDataType: dynamicDataType || option.dynamicDataType
      }));
    } else if (cardBrand === 'MASTERCARD') {
      paymentOptions = dynamicDataType
        ? { ...paymentOptions[0], dynamicDataType }
        : paymentOptions[0];
    }
  }

  if (modifiedInput.dpaTransactionOptions) {
    modifiedInput.dpaTransactionOptions.paymentOptions = paymentOptions;
  }
}

function handleCardBrandSpecifics(rawInput: CheckoutData, cardBrand: string | null): CheckoutData {
  const modifiedInput = { ...rawInput };

  if (cardBrand === 'VISA') {
    decorateVisaCheckoutData(modifiedInput);
  }

  if (!cardBrand && !rawInput.encryptedCard) {
    throw error('CARD_NOT_RECOGNIZED', 'Card brand cannot be null');
  }

  const dynamicDataType = getDynamicDataType(modifiedInput, cardBrand);
  updatePaymentOptions(modifiedInput, cardBrand, dynamicDataType);

  return modifiedInput;
}

async function performCheckout(
  modifiedInput: CheckoutData,
  cardBrand: string | null,
  rawInput: CheckoutData
): Promise<any> {
  const dcf = getState().srcSdks.loadedSdk as { [key: string]: any };
  const initData = getState().srcSdks.apiResponseCache.initData as {
    [key: string]: { dpaTransactionOptions?: any };
  };

  const initDpaTransactionOptions = initData[cardBrand as string]?.dpaTransactionOptions;

  modifiedInput.dpaTransactionOptions = modifiedInput.dpaTransactionOptions || {};

  if (initDpaTransactionOptions) {
    addMissingProperties(modifiedInput.dpaTransactionOptions, initDpaTransactionOptions);
  }

  if (
    !modifiedInput.payloadTypeIndicatorCheckout &&
    initDpaTransactionOptions?.payloadTypeIndicator
  ) {
    modifiedInput.payloadTypeIndicatorCheckout = initDpaTransactionOptions.payloadTypeIndicator;
  }

  const isLtaApiEnabled = (window as any)?.VISA_FT?.isLtaApiEnabled;
  const isUctpLtaEnabled = (window as any)?.VISA_FT?.isUctpLtaEnabled;
  const isUatEnabled = getState().uatStatus;
  let checkoutResponse;
  /*eslint-disable*/
  console.log(`UCTP --> isUctpLtaEnabled: ${isUctpLtaEnabled}, isLtaApiEnabled: ${isLtaApiEnabled}`);
  const shouldUseLtaFlow = isUctpLtaEnabled && isLtaApiEnabled && isUatEnabled && modifiedInput.encryptedCard;
  if (shouldUseLtaFlow) {
    console.log('+++ UCTP shouldUseLtaFlow --->>> ', shouldUseLtaFlow);
    markStart(`vsb_checkout_lta`);
    // uCTP with LTA flow
    /*eslint-disable*/
    const encryptedNwResponse = await dcf.VISA.getEncryptedNwPayload(modifiedInput);
    const { cardBrand: ltaCardBrand, encryptedCard: ltaEncryptedCard } =
      encryptedNwResponse?.data?.data || {};
    const ltaModifiedInput = { ...modifiedInput, encryptedCard: ltaEncryptedCard };
    if (ltaCardBrand === 'MASTERCARD') {
      markStart(`vsb_checkout_lta_${ltaCardBrand}`);
      checkoutResponse = await dcf[ltaCardBrand as string].checkout(ltaModifiedInput);
    } else if(ltaCardBrand === 'VISA') {
      markStart(`vsb_checkout_lta_${ltaCardBrand}`);
      //No change needed in the request payload for VISA, so using the original modifiedInput
      checkoutResponse = await dcf[ltaCardBrand as string].checkout(modifiedInput);
    } else {
      console.log('Failure to process the encrypted network payload');
      return error('SERVER_ERROR', 'Failure to process the encrypted network payload', 'Failure to process the request payload' as any);
    }
    console.log('UCTP LTA >>> handleCheckoutResponse');
    return handleCheckoutResponse(checkoutResponse, rawInput, ltaCardBrand, modifiedInput);
  } else {
    //BAU Flow
    checkoutResponse = await dcf[cardBrand as string].checkout(modifiedInput);
    markEnd(`vsb_checkout_${cardBrand}`);
    return handleCheckoutResponse(checkoutResponse, rawInput, cardBrand, modifiedInput);
  }
}

function handleError(e: any): any {
  if ('reason' in e) {
    return error('SERVER_ERROR', e.reason, e.message as any);
  }
  return e;
}

export default async function checkout(rawInput: CheckoutData & { idToken?: string }) {
  const isAddCard = !rawInput.srcDigitalCardId && rawInput.encryptedCard;
  // START GENAI@GHCOPILOT
  // START GENAI@CLINE
  // https://issues.trusted.visa.com/browse/TLPERF1-7573
  // To avoid popup blockers in Safari in add card for MC need to pre-open a blank popup window and pass that reference to visa-sdk.
  if (getBrowserName()?.toLowerCase() === 'safari' && !rawInput.windowRef && isAddCard) {
    // Process the window reference through onInitialPopUpOpen
    rawInput.windowRef = onInitialPopUpOpen(rawInput) ?? undefined;
  }
  // END GENAI@CLINE
  // END GENAI@GHCOPILOT

  try {
    if (validateInitResponses()) {
      // START GENAI@CLINE
      closePopupWindowAndReset({} as CheckoutData, rawInput);
      // END GENAI@CLINE
      return error('AUTH_INVALID', 'Invalid federated id token.', ' ' as any);
    }

    const isReturningUser = checkReturningUser();
    validateInput(rawInput, isReturningUser);
    setIdToken(rawInput, isReturningUser);
    setConsumerLocale(rawInput);

    const cardBrand = determineCardBrand(rawInput);
    validateCardBrand(cardBrand);

    const modifiedInput = handleCardBrandSpecifics(rawInput, cardBrand);

    return await performCheckout(modifiedInput, cardBrand, rawInput);
  } catch (e: any) {
    // Close popup window if it exists and reset the window reference
    // START GENAI@CLINE
    closePopupWindowAndReset({} as CheckoutData, rawInput);
    // END GENAI@CLINE
    return handleError(e);
  }
}

// START GENAI@CLINE
/**
 * https://issues.trusted.visa.com/browse/TLPERF1-7573 To avoid popup blockers in Safari in add card for MC,
 * we open a blank popup window at the start of the checkout flow and pass that windowRef to visa-sdk.
 * To avoid conflicting with select-card flows and stepups we should only perform below logic when in an add card scenario
 * Creates a popup window for Safari browsers
 * @param data Checkout data
 * @returns Window reference or null
 */
function onInitialPopUpOpen(data: CheckoutData): Window | null {
  if (!data.windowRef) {
    // START GENAI@CLINE
    try {
      // Use about:blank as initial URL - will be navigated to proper URL by checkout flow
      const initialUrl = 'about:blank';
      const windowName = 'Click To Pay';
      const windowProps = getPopupWindowProperties();
      // Attempt to open the window
      const popupWindow = window.open(initialUrl, windowName, windowProps);
      // Check if popup was blocked
      if (!popupWindow) {
        console.warn('Popup window creation failed - popup may have been blocked');
      }
      return popupWindow;
    } catch (err) {
      console.error('Error creating Popup window:', err);
      return null;
    }
    // END GENAI@CLINE
  }

  return data.windowRef;
}

/**
 * Gets popup window properties string with responsive width and height
 * @returns Window properties string
 */
// START GENAI@CLINE
function getPopupWindowProperties(): string {
  // START GENAI@CLINE
  // Get screen dimensions
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;

  // Calculate responsive dimensions (60% of screen width, 80% of screen height)
  let width = Math.round(screenWidth * 0.6);
  let height = Math.round(screenHeight * 0.8);

  // Set minimum and maximum dimensions
  const minWidth = 200;
  const maxWidth = 400;
  const minHeight = 400;
  const maxHeight = 600;

  // Ensure dimensions are within bounds
  width = Math.max(minWidth, Math.min(width, maxWidth));
  height = Math.max(minHeight, Math.min(height, maxHeight));

  return `width=${width},height=${height},menubar=no,toolbar=no,location=no,resizable=yes,scrollbars=yes,status=no,dependent=yes`;
  // END GENAI@CLINE
}
// END GENAI@CLINE
// END GENAI@CLINE

async function handleCheckoutResponse(
  checkoutResponse: any,
  rawInput: CheckoutData,
  cardBrand: string | null,
  modifiedInput: CheckoutData
): Promise<any> {
  // === TLVS1-96 DEBUG: Processing checkout response ===
  /*eslint-disable-next-line */
  console.log('[VSB] [TLVS1-96] === PROCESSING CHECKOUT RESPONSE ===', {
    cardBrand,
    dcfActionCode: checkoutResponse?.dcfActionCode,
    hasCheckoutResponse: !!checkoutResponse?.checkoutResponse,
    willReturnActionCode: getCheckoutActionCode(checkoutResponse)
  });

  const communicatorState = getState() as CommunicatorState;
  let apiResponseCache = communicatorState.srcSdks.apiResponseCache;

  // START GENAI@CLINE
  // Determine if we need to close the popup window based on browser and response conditions
  if (shouldClosePopupWindow(checkoutResponse)) {
    closePopupWindowAndReset(modifiedInput, rawInput);
  }
  // END GENAI@CLINE
  //START GENAI@GHCOPILOT

  if (checkoutResponse.checkoutResponse) {
    if (cardBrand === 'VISA') {
      const dcfCheckoutResponse = checkoutResponse.checkoutResponse;
      //START GENAI@GHCOPILOT
      const dcfBindingStatus: BindingStatus = checkoutResponse?.unbindAppInstance
        ? 'UNBIND'
        : 'BIND';
      //END GENAI@GHCOPILOT
      if (checkoutResponse.dcfActionCode === 'COMPLETE') {
        apiResponseCache = clearApiResponseCache(apiResponseCache);
      }
      saveApiResponseCache(apiResponseCache);

      // === TLVS1-96 DEBUG: Returning VISA checkout result ===
      const finalActionCode = getCheckoutActionCode(checkoutResponse);
      /*eslint-disable-next-line */
      console.log('[VSB] [TLVS1-96] === RETURNING VISA CHECKOUT RESULT ===', {
        actionCode: finalActionCode,
        bindingStatus: dcfBindingStatus,
        dcfActionCode: checkoutResponse.dcfActionCode
      });

      return {
        checkoutResponse: dcfCheckoutResponse,
        actionCode: finalActionCode,
        bindingStatus: dcfBindingStatus
      };
    } else {
      // START GENAI@CLINE
      return handleNonVisaCheckoutResponse(checkoutResponse, modifiedInput, cardBrand, rawInput);
      // END GENAI@CLINE
    }
  } else if (checkoutResponse?.data?.cardBrand && checkoutResponse?.data?.cardBrand !== 'VISA') {
    const { cardBrand, encryptedCard } = checkoutResponse.data;
    checkoutResponse.encryptedCard = encryptedCard || {};
    checkoutResponse.encryptedCard = encryptedCard || {};

    return processNetworkCheckout(checkoutResponse, rawInput, cardBrand);
  } else if (checkoutResponse?.dcfActionCode) {
    return {
      actionCode: getCheckoutActionCode(checkoutResponse),
      bindingStatus: checkoutResponse?.unbindAppInstance ? 'UNBIND' : 'BIND'
    };
  } else {
    // START GENAI@CLINE
    closePopupWindowAndReset(modifiedInput, rawInput);
    // END GENAI@CLINE
    return resolveWithError('UCTP ADD CARD Error');
  }
}

// START GENAI@CLINE
/**
 * Determines if the popup window should be closed based on browser and response conditions
 * @param checkoutResponse The response from the checkout operation
 * @returns True if the popup window should be closed
 */
function shouldClosePopupWindow(checkoutResponse: any): boolean {
  // Check if we're running in Safari browser
  const isSafariBrowser = getBrowserName()?.toLowerCase() === 'safari';
  // START GENAI@CLINE
  // Check for Safari-specific card change or cancel actions
  // START GENAI@CLINE
  const isSafariCardAction =
    isSafariBrowser &&
    checkoutResponse?.dcfActionCode &&
    ['ADD_CARD', 'CANCEL', 'CHANGE_CARD', 'COMPLETE', 'ERROR', 'SWITCH_CONSUMER'].includes(
      checkoutResponse?.dcfActionCode
    );
  // END GENAI@CLINE
  // END GENAI@CLINE
  // Return true if any condition is met
  return isSafariCardAction;
}
// END GENAI@CLINE

// START GENAI@CLINE
/**
 * https://issues.trusted.visa.com/browse/TLPERF1-7573 To avoid popup blockers in Safari in add card for MC,
 * we open a blank popup window at the start of the checkout flow and pass that windowRef to visa-sdk.
 * To avoid conflicting with select-card flows and stepups we should only perform below logic when in an add card scenario
 * Closes popup window if it exists and resets the windowRef
 * @param modifiedInput Primary checkout data
 * @param rawInput Optional secondary checkout data
 */
function closePopupWindowAndReset(modifiedInput: CheckoutData, rawInput?: CheckoutData): void {
  const isAddCard = !rawInput?.srcDigitalCardId && rawInput?.encryptedCard;
  if (!isAddCard) {
    return;
  }
  if (modifiedInput.windowRef && typeof modifiedInput.windowRef.close === 'function') {
    modifiedInput.windowRef.close();
    modifiedInput.windowRef = undefined;
  } else if (rawInput && rawInput.windowRef && typeof rawInput.windowRef.close === 'function') {
    rawInput.windowRef.close();
    rawInput.windowRef = undefined;
  } else if (window.opener) {
    // If we're in a popup, close ourselves
    window.close();
  }
}
// END GENAI@CLINE
async function handleNonVisaCheckoutResponse(
  checkoutResponse: any,
  modifiedInput: CheckoutData,
  cardBrand: string | null,
  rawInput?: CheckoutData
): Promise<any> {
  let dcfCheckoutResponse;
  //To handle enroll(add card) and checkout (select card) flow
  if (checkoutResponse?.checkoutResponse?.dcfActionCode) {
    dcfCheckoutResponse = checkoutResponse.checkoutResponse;
  } else {
    dcfCheckoutResponse = checkoutResponse;
  }
  const dcfBindingStatus: BindingStatus = dcfCheckoutResponse.unbindAppInstance ? 'UNBIND' : 'BIND';
  const apiResponseCache = getState().srcSdks.apiResponseCache;

  // @ts-ignore
  // eslint-disable-next-line
  let intentId = apiResponseCache.getSrcProfile['VISA']?.srcCorrelationId ?? '';
  const isUatEnabled = getState().uatStatus;

  if (isUatEnabled) {
    intentId = '';
  }

  if (dcfCheckoutResponse.dcfActionCode === 'CANCEL') {
    // START GENAI@CLINE
    // Close popup window if it exists and reset windowRef to undefined
    closePopupWindowAndReset(modifiedInput, rawInput);
    // END GENAI@CLINE
    return {
      actionCode: getCheckoutActionCode(dcfCheckoutResponse),
      bindingStatus: dcfBindingStatus
    };
  }

  // @ts-ignore
  const sdkResponse = await window.VISA_SDK.paymentData({
    apikey: dpaId,
    createIntentRequestStr: generateCreateIntentRequestStr(modifiedInput),
    idToken: dcfCheckoutResponse.idToken,
    intentId: intentId || '',
    network: cardBrand,
    payloadJWS:
      dcfCheckoutResponse.checkoutResponseSignature || dcfCheckoutResponse.checkoutResponseJws || ''
  });

  if (!sdkResponse.data?.data?.checkoutResponse) {
    // START GENAI@CLINE
    // Close popup window if it exists and reset windowRef to undefined
    closePopupWindowAndReset(modifiedInput, rawInput);
    // END GENAI@CLINE

    throw error('SERVER_ERROR', 'General server error', '' as any);
  }

  if (dcfCheckoutResponse.dcfActionCode === 'COMPLETE') {
    clearApiResponseCache(apiResponseCache);

    // START GENAI@CLINE
    // Close popup window if it exists
    // Start GENAI@CLINE
    closePopupWindowAndReset(modifiedInput, rawInput);
    // END GENAI@CLINE
    // END GENAI@CLINE
  }

  return {
    checkoutResponse: sdkResponse.data.data.checkoutResponse,
    actionCode: getCheckoutActionCode(dcfCheckoutResponse),
    bindingStatus: dcfBindingStatus
  };
}

async function processNetworkCheckout(
  checkoutResponse: any,
  rawInput: CheckoutData,
  cardBrand: string | null
): Promise<any> {
  try {
    const dynamicDataType = getDynamicDataType(rawInput, cardBrand);
    updatePaymentOptions(rawInput, cardBrand, dynamicDataType);

    const initData = getState().srcSdks.apiResponseCache.initData as {
      [key: string]: { dpaTransactionOptions?: any };
    };
    const initDpaTransactionOptions = initData[cardBrand as string]?.dpaTransactionOptions;

    rawInput.dpaTransactionOptions = rawInput.dpaTransactionOptions || {};
    if (initDpaTransactionOptions) {
      addMissingProperties(rawInput.dpaTransactionOptions, initDpaTransactionOptions);
    }

    if (!rawInput.payloadTypeIndicatorCheckout && initDpaTransactionOptions?.payloadTypeIndicator) {
      rawInput.payloadTypeIndicatorCheckout = initDpaTransactionOptions.payloadTypeIndicator;
    }

    const dcf = getState().srcSdks.loadedSdk as { [key: string]: any };
    const cardEnrollNetwork = checkoutResponse?.data?.cardBrand;
    const dfcNetwork = checkoutResponse.cardNetwork;
    const networkToConnect = cardEnrollNetwork ?? dfcNetwork;
    rawInput = { ...rawInput, encryptedCard: checkoutResponse.encryptedCard }; //actual needed

    const networkCheckoutResponse = await dcf[networkToConnect].checkout(rawInput);

    if (networkCheckoutResponse.error) {
      // START GENAI@CLINE
      closePopupWindowAndReset({} as CheckoutData, rawInput);
      // END GENAI@CLINE
      return resolveWithError(networkCheckoutResponse.error);
    }

    const dcfCheckoutResponse = networkCheckoutResponse;
    const dcfBindingStatus: BindingStatus = dcfCheckoutResponse.unbindAppInstance
      ? 'UNBIND'
      : 'BIND';

    const intentIdThroughEnroll = networkCheckoutResponse?.checkoutResponse?.srcCorrelationId;
    const intentIdByVisa = checkoutResponse?.data?.srcCorrelationId;
    //const cachedIntentId = apiResponseCache.getSrcProfile['VISA']?.srcCorrelationId ?? '';

    /* START GENAI@GHCOPILOT */
    // @ts-ignore
    // eslint-disable-next-line
    const cachedIntentId = ''; //apiResponseCache.getSrcProfile['VISA']?.srcCorrelationId ?? ''; //it was intentId
    const intentId = intentIdByVisa || cachedIntentId || intentIdThroughEnroll;
    /* END GENAI@GHCOPILOT */
    const srciUser = 'NEW_USER';

    if (dcfCheckoutResponse.dcfActionCode === 'CANCEL') {
      // START GENAI@CLINE
      // Close popup window if it exists
      closePopupWindowAndReset({} as CheckoutData, rawInput);
      // END GENAI@CLINE

      return {
        actionCode: getCheckoutActionCode(dcfCheckoutResponse),
        bindingStatus: dcfBindingStatus
      };
    }

    //END GENAI@GHCOPILOT

    // @ts-ignore
    const sdkResponse = await window.VISA_SDK.paymentData({
      apikey: dpaId,
      createIntentRequestStr: generateCreateIntentRequestStr(rawInput),
      idToken: srciUser !== 'NEW_USER' ? dcfCheckoutResponse.idToken : '',
      intentId,
      network: cardBrand,
      payloadJWS:
        dcfCheckoutResponse.checkoutResponseSignature ||
        dcfCheckoutResponse.checkoutResponseJws ||
        ''
    });

    if (!sdkResponse.data?.data?.checkoutResponse) {
      // Close popup window if it exists
      closePopupWindowAndReset({} as CheckoutData, rawInput);

      throw error('SERVER_ERROR', 'General server error', '' as any);
    }

    /* START GENAI@GHCOPILOT */
    if (dcfCheckoutResponse.dcfActionCode === 'COMPLETE') {
      clearApiResponseCache(getState().srcSdks.apiResponseCache);

      // START GENAI@CLINE
      // Close popup window if it exists
      closePopupWindowAndReset({} as CheckoutData, rawInput);
      // END GENAI@CLINE
    }
    /* END GENAI@GHCOPILOT */

    return {
      checkoutResponse: sdkResponse.data.data.checkoutResponse,
      actionCode: getCheckoutActionCode(dcfCheckoutResponse),
      bindingStatus: dcfBindingStatus
    };
  } catch (error) {
    // START GENAI@CLINE
    closePopupWindowAndReset({} as CheckoutData, rawInput);
    // END GENAI@CLINE
    return resolveWithError(error);
  }
}

function clearApiResponseCache(cache: any): any {
  return { ...cache, email: '', getSrcProfile: {}, idTokens: {} };
}

function resolveWithError(error: any): any {
  return { actionCode: 'ERROR', error };
}

//END GENAI@GHCOPILOT
//END GENAI@CHATGPT4
//END GENAI@CHATGPT4
//END GENAI@GHCOPILOT
